import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  CLIENT_PATH_TOKEN,
  COMPETITOR_PATH_TOKEN,
  CREATIVE_PATH_MESSAGE,
  ClientPathSchema,
  CompetitorsPathSchema,
  resolveCreativePath,
} from "@nexus/contracts";
import { Button, Input } from "@nexus/ui";
import {
  SettingRow,
  SettingsCard,
  SettingsSaveFooter,
} from "@/features/changelog/components/shared/SettingsCard";
import { ApiError } from "@/lib/api-client";
import { systemSettingsQueryOptions, useSaveSystemSettings } from "@/lib/queries/system-settings.queries";

function parsedClientPath(value: string): string | null {
  const parsed = ClientPathSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parsedCompetitorsPath(value: string): string | null {
  const parsed = CompetitorsPathSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function SystemSettingsPage() {
  const { t } = useTranslation();
  const query = useQuery(systemSettingsQueryOptions());
  const save = useSaveSystemSettings();
  const [draftClient, setDraftClient] = useState("");
  const [draftCompetitors, setDraftCompetitors] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query.data) return;
    setDraftClient(query.data.clientPath);
    setDraftCompetitors(query.data.competitorsPath);
  }, [query.data]);

  if (query.isLoading) {
    return <p className="px-5 py-6 text-sm text-muted-foreground">{t("admin.systemSettings.loading", "Loading…")}</p>;
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex flex-col gap-3 px-5 py-6">
        <p className="m-0 text-sm text-destructive">{t("admin.systemSettings.loadError", "Could not load system settings.")}</p>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {t("admin.systemSettings.retry", "Try again")}
        </Button>
      </div>
    );
  }

  const settings = query.data;
  const parsedClient = parsedClientPath(draftClient);
  const parsedCompetitors = parsedCompetitorsPath(draftCompetitors);
  const clientError = parsedClient ? null : t("admin.systemSettings.invalidPath", CREATIVE_PATH_MESSAGE);
  const competitorsError = parsedCompetitors ? null : t("admin.systemSettings.invalidPath", CREATIVE_PATH_MESSAGE);
  const validationError = clientError ?? competitorsError;
  const isDirty =
    (parsedClient ?? draftClient) !== settings.clientPath ||
    (parsedCompetitors ?? draftCompetitors) !== settings.competitorsPath;
  const exampleClient = resolveCreativePath(parsedClient ?? settings.clientPath, CLIENT_PATH_TOKEN, "acme");
  const exampleCompetitors = resolveCreativePath(
    parsedCompetitors ?? settings.competitorsPath,
    COMPETITOR_PATH_TOKEN,
    "RivalCo",
  );

  const credit = settings.updatedByName
    ? t("admin.systemSettings.lastChanged", "Last changed by {{name}}", { name: settings.updatedByName })
    : t("admin.systemSettings.neverChanged", "Using the defaults. Nobody has saved these paths yet.");

  async function handleSave() {
    const clientPath = parsedClientPath(draftClient);
    const competitorsPath = parsedCompetitorsPath(draftCompetitors);
    if (!clientPath || !competitorsPath) {
      setError(t("admin.systemSettings.invalidPath", CREATIVE_PATH_MESSAGE));
      return;
    }
    setError(null);
    try {
      await save.mutateAsync({ clientPath, competitorsPath, expectedRevision: settings.revision });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setError(
          t(
            "admin.systemSettings.staleError",
            "Creative paths were saved somewhere else. Reload this page and try again.",
          ),
        );
        return;
      }
      setError(t("admin.systemSettings.saveError", "Could not save system settings."));
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <SettingsCard
          icon="link"
          title={t("admin.systemSettings.title", "Creative paths")}
          description={t(
            "admin.systemSettings.description",
            "HTTPS prefixes the app uses to build creative file URLs. A {client} or {competitor} token is replaced; otherwise the name is appended. No trailing slash.",
          )}
        >
          <SettingRow
            htmlFor="system-client-path"
            label={t("admin.systemSettings.clientLabel", "Client path")}
            description={t(
              "admin.systemSettings.clientHint",
              "Default is the public bucket. A CDN template looks like https://cdn.adge.ai/{client}.",
            )}
            control={
              <Input
                id="system-client-path"
                value={draftClient}
                spellCheck={false}
                className="h-8 w-[28rem] max-w-full text-xs"
                onChange={(event) => {
                  setDraftClient(event.target.value);
                  setError(null);
                }}
              />
            }
          />
          <SettingRow
            htmlFor="system-competitors-path"
            label={t("admin.systemSettings.competitorsLabel", "Competitors path")}
            description={t(
              "admin.systemSettings.competitorsHint",
              "Default includes pub_data. A CDN template looks like https://cdn.adge.ai/competitors/{competitor}.",
            )}
            control={
              <Input
                id="system-competitors-path"
                value={draftCompetitors}
                spellCheck={false}
                className="h-8 w-[28rem] max-w-full text-xs"
                onChange={(event) => {
                  setDraftCompetitors(event.target.value);
                  setError(null);
                }}
              />
            }
          />
          <div className="flex flex-col gap-1 px-5 py-3 text-xs text-muted-foreground">
            <p className="m-0">
              {t("admin.systemSettings.clientExample", "Client files: {{prefix}}/thumbnails/…", {
                prefix: exampleClient,
              })}
            </p>
            <p className="m-0">
              {t("admin.systemSettings.competitorExample", "Competitor files: {{prefix}}/thumbnails/…", {
                prefix: exampleCompetitors,
              })}
            </p>
          </div>
        </SettingsCard>
      </div>
      <SettingsSaveFooter
        status={error ?? (validationError && isDirty ? validationError : credit)}
      >
        <Button
          type="button"
          disabled={!isDirty || Boolean(validationError) || save.isPending}
          onClick={() => void handleSave()}
        >
          {save.isPending
            ? t("admin.systemSettings.saving", "Saving…")
            : t("admin.systemSettings.save", "Save")}
        </Button>
      </SettingsSaveFooter>
    </div>
  );
}
